// Note commands (notes/{id} meta + notes/{id}/chunks/body). Pure; see docs/plans/notes.md §3.
import {
  bodyMeta, escapeHtml, findSection, linksPatch, noteLinks, notebookForKind, notebookSections, sectionsAdd, sectionsMove, sectionsRemove, sectionsRename, textToHtml,
  notebookAccess, notebookAccessPatch, noteAccessOverride, noteAccessPatch, sameAccess,
} from '../notes.js';
import { localDateStr } from '../time.js';
import { set, update } from './writes.js';
import { bodySid } from '../notesync.js';

const now = (ctx) => (typeof ctx.now === 'function' ? ctx.now() : ctx.now ?? Date.now());

/** A new note: `text` (plain) or `html`; links = [{type:'project'|'task', id}] or projectId / taskId. */
export function addNote(ctx, note = {}) {
  const at = now(ctx);
  const id = note.id || ctx.newId();
  const html = note.html ?? textToHtml(note.text ?? note.content ?? '');
  const kind = note.kind || 'note';
  if (!note.title && !html) throw new Error('title or text required');
  const meta = {
    id, title: note.title || '', icon: note.icon || null,
    notebookId: note.notebookId || notebookForKind(kind),
    ...(note.sectionId ? { sectionId: note.sectionId } : {}),
    ...(note.private ? { private: true } : {}),
    ...linksPatch(noteLinks(note)),
    tags: Array.isArray(note.tags) ? note.tags : [],
    date: note.date || localDateStr(at), kind, pinned: !!note.pinned, deletedAt: null,
    bodyRev: 1, ...bodyMeta(html), createdAt: at, updatedAt: at,
  };
  return { writes: [set(`notes/${id}`, meta), set(`notes/${id}/chunks/body`, { html, rev: 1, savedAt: at, sid: bodySid(at), base: null })], note: meta };
}

/**
 * Append to a note's body. `body` = the current chunk ({html, rev}) or null for a note with only
 * legacy `content`. Bumps bodyRev; the caller passes coalesce:false (this is not autosave).
 */
export function appendNote(ctx, { id, text, html, body, note }) {
  if (!note) throw new Error(`no note ${id}`);
  if (note.locked) throw new Error('that note is locked — encrypted; only its owner can change it, in the app');
  const add = html ?? textToHtml(text || '');
  if (!add) throw new Error('text required');
  const curHtml = body?.html ?? textToHtml(note.content || '');
  const nextHtml = curHtml ? `${curHtml}${add}` : add;
  const rev = (body?.rev || note.bodyRev || 0) + 1;
  const at = now(ctx);
  const meta = { ...bodyMeta(nextHtml), bodyRev: rev, updatedAt: at };
  return { writes: [set(`notes/${id}/chunks/body`, { html: nextHtml, rev, savedAt: at, sid: bodySid(at), base: body?.sid || null }), update(`notes/${id}`, meta)], rev };
}

/**
 * Replace text in a note's body html (exact match). `find` must occur once unless `all`; `replace` may be ''.
 * Same shape of writes as appendNote: new body chunk + refreshed text copy + bumped rev.
 */
export function editNote(ctx, { id, find, replace = '', all = false, body, note }) {
  if (!note) throw new Error(`no note ${id}`);
  if (note.locked) throw new Error('that note is locked — encrypted; only its owner can change it, in the app');
  if (!find) throw new Error('find required');
  const curHtml = body?.html ?? textToHtml(note.content || '');
  // As given, or as escaped text (quotes / & may be entities in the stored html).
  const forms = [find, escapeHtml(find), escapeHtml(find).replace(/&quot;/g, '"').replace(/&#39;/g, "'")];
  const key = forms.find((f) => curHtml.includes(f));
  if (!key) throw new Error('find text not in the note');
  const hits = curHtml.split(key).length - 1;
  if (hits > 1 && !all) throw new Error(`find text is in the note ${hits} times — make it longer, or pass all`);
  const nextHtml = curHtml.split(key).join(replace);
  const rev = (body?.rev || note.bodyRev || 0) + 1;
  const at = now(ctx);
  const meta = { ...bodyMeta(nextHtml), bodyRev: rev, updatedAt: at };
  return { writes: [set(`notes/${id}/chunks/body`, { html: nextHtml, rev, savedAt: at, sid: bodySid(at), base: body?.sid || null }), update(`notes/${id}`, meta)], rev, replaced: hits };
}

/** A new notebook (notebooks/{id}), same fields the phone writes. parentId = nest it in another notebook. */
export function addNotebook(ctx, { name, icon = '📓', color = '#FB5446', parentId = null, access = null } = {}) {
  const nm = String(name ?? '').trim();
  if (!nm) throw new Error('name required');
  const at = now(ctx);
  const id = ctx.newId();
  const notebook = { id, name: nm, icon, color, parentId, ...notebookAccessPatch(access ?? { mode: 'team' }), sortOrder: at, archivedAt: null, createdAt: at };
  return { writes: [set(`notebooks/${id}`, notebook)], notebook };
}

/** A section at the end of a notebook's sections. */
export function addNotebookSection(ctx, { id, notebooks = [], name }) {
  const nb = notebooks.find((n) => n.id === id);
  if (!nb) throw new Error(`no notebook ${id}`);
  const sid = ctx.newId();
  const sections = sectionsAdd(nb.sections, { id: sid, name });
  const at = now(ctx);
  return { writes: [update(`notebooks/${id}`, { sections, updatedAt: at })], section: sections.find((x) => x.id === sid), sections };
}

/**
 * Rename (`name`), reorder (`move` -1 up / +1 down) or delete (`remove`) a notebook's section. Deleting moves its
 * notes to unsectioned (pass the notebook's `notes`).
 */
export function updateNotebookSection(ctx, { id, notebooks = [], notes = [], sectionId, name, move, remove = false }) {
  const nb = notebooks.find((n) => n.id === id);
  if (!nb) throw new Error(`no notebook ${id}`);
  const sec = findSection(nb, sectionId);
  if (!sec) throw new Error(`no section ${sectionId} in ${nb.name}`);
  let sections = notebookSections(nb);
  const at = now(ctx);
  const writes = [];
  if (remove) {
    sections = sectionsRemove(sections, sec.id);
    for (const n of notes) if (n.notebookId === id && n.sectionId === sec.id) writes.push(update(`notes/${n.id}`, { sectionId: null, updatedAt: at }));
  } else {
    if (name !== undefined) sections = sectionsRename(sections, sec.id, name);
    if (move) sections = sectionsMove(sections, sec.id, move);
    if (name === undefined && !move) throw new Error('nothing to update');
  }
  writes.unshift(update(`notebooks/${id}`, { sections, updatedAt: at }));
  return { writes, sections, section: sections.find((x) => x.id === sec.id) || null };
}

/**
 * Rename / re-icon / move a notebook. parentId null = top level; a notebook can't go inside itself or its own notebooks.
 * `access` (docs/SYNC.md §3.10) → the clean shape + the `visibility` mirror; pass the org's `notes` so each one in it
 * (without its own access) follows — `private` + `accessAt`, so the server re-files it. `touched` = those note ids (the
 * caller re-saves their bodies too).
 */
export function updateNotebook(ctx, { id, notebooks = [], notes = [], ...patch }) {
  const cur = notebooks.find((n) => n.id === id);
  if (!cur) throw new Error(`no notebook ${id}`);
  const data = {};
  for (const k of ['name', 'icon', 'color', 'parentId', 'archivedAt']) if (patch[k] !== undefined) data[k] = k === 'name' ? String(patch[k]).trim() : patch[k];
  if ('name' in data && !data.name) throw new Error('name required');
  if (data.parentId) {
    const under = new Set([id]);
    let grew = true;
    while (grew) { grew = false; for (const n of notebooks) if (n.parentId && under.has(n.parentId) && !under.has(n.id)) { under.add(n.id); grew = true; } }
    if (under.has(data.parentId)) throw new Error('a notebook can\'t go inside itself or one of its own notebooks');
  }
  const touched = [];
  const noteWrites = [];
  if (patch.access !== undefined && !sameAccess(notebookAccess(cur), patch.access)) {
    Object.assign(data, notebookAccessPatch(patch.access));
    const at = now(ctx);
    for (const n of notes) {
      if (n.notebookId !== id || noteAccessOverride(n)) continue;
      noteWrites.push(update(`notes/${n.id}`, { private: data.access.mode === 'private', accessAt: at }));
      touched.push(n.id);
    }
  }
  if (!Object.keys(data).length) throw new Error('nothing to update');
  data.updatedAt = now(ctx);
  return { writes: [update(`notebooks/${id}`, data), ...noteWrites], patch: data, touched };
}

/** One note's own access (§3.10): an access set, or null = the same as its notebook. The caller re-saves its body. */
export function setNoteAccess(ctx, { id, note, notebook = null, access = null }) {
  if (!note) throw new Error(`no note ${id}`);
  return { writes: [update(`notes/${id}`, { ...noteAccessPatch(access, notebook), accessAt: now(ctx), updatedAt: now(ctx) })] };
}

const N_EDITABLE = new Set(['title', 'icon', 'notebookId', 'sectionId', 'private', 'tags', 'date', 'pinned', 'deletedAt', 'kind']);

/** Metadata only; `links` replaces the link set. deletedAt = now → Trash (soft). A new notebook drops the section unless one is given. */
export function updateNote(ctx, { id, links, note, ...patch }) {
  if (note?.locked && patch.title !== undefined) throw new Error('that note is locked — its title is encrypted; only its owner can change it, in the app');
  const data = {};
  for (const [k, v] of Object.entries(patch)) if (N_EDITABLE.has(k) && v !== undefined) data[k] = v;
  if (data.notebookId !== undefined && data.sectionId === undefined) data.sectionId = null;
  if (links !== undefined) Object.assign(data, linksPatch(links));
  if (!Object.keys(data).length) throw new Error('nothing to update');
  data.updatedAt = now(ctx);
  return { writes: [update(`notes/${id}`, data)], patch: data };
}

export const trashNote = (ctx, { id }) => updateNote(ctx, { id, deletedAt: now(ctx) });
